import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { errMsg, isGatewayError } from "@/lib/api";
import { Loader2 } from "lucide-react";

import { brand } from "@/lib/brand";
const HERO = brand.hero;

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [username, setU] = useState("");
  const [password, setP] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    for (let attempt = 0; attempt < 3; attempt++) {
      try { await login(username, password); nav("/"); return; }
      catch (ex) {
        if (isGatewayError(ex) && attempt < 2) { setErr("Server is waking up — retrying…"); await new Promise((r) => setTimeout(r, 3000)); continue; }
        setErr(errMsg(ex)); break;
      }
    }
    setBusy(false);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr]">
      <div className="relative hidden lg:block bg-[#111827] overflow-hidden">
        <img src={HERO} alt={`${brand.name} retail store`} data-testid="login-hero-image" className="absolute inset-0 h-full w-full object-cover opacity-55" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#111827] via-[#111827]/60 to-transparent" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12">
          <div className="flex items-center gap-4"><img src={brand.logoWhite} alt={brand.name} className={`${brand.loginLogoClass} w-auto`} data-testid="login-logo" /><span className="font-display text-white font-semibold text-lg border-l border-white/30 pl-4">SIS Sales Intelligence</span></div>
          <div className="max-w-lg">
            <h1 className="font-display text-4xl sm:text-5xl font-bold text-white leading-tight">Every partner file. Standardised. Validated. Trusted.</h1>
            <p className="mt-4 text-slate-300 text-base">Deterministic recognition of 10 SIS partner formats, human-approved corrections, full traceability from report back to source row.</p>
            <div className="mt-8 grid grid-cols-3 gap-4">
              {[["10", "SIS partners"], ["15", "reports"], ["100%", "audit coverage"]].map(([v, l]) => <div key={l} className="border-l-2 border-[#000000] pl-3"><div className="font-display text-2xl font-bold text-white">{v}</div><div className="text-xs text-slate-400 uppercase tracking-wider">{l}</div></div>)}
            </div>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-center p-8 bg-white">
        <form onSubmit={submit} className="w-full max-w-sm fade-up" data-testid="login-form">
          <img src={brand.logo} alt={brand.name} className="h-8 w-auto mb-6 lg:hidden" />
          <div className="h-1 w-12 bg-[#000000] mb-6" />
          <h2 className="font-display text-2xl font-bold text-slate-900">Sign in</h2>
          <p className="text-sm text-slate-500 mt-1">Use your {brand.name} SIS platform credentials.</p>
          <div className="mt-8 space-y-4">
            <label className="block"><span className="label-caps">Username or email</span>
              <input data-testid="login-username-input" value={username} onChange={(e) => setU(e.target.value)} autoFocus className="mt-1 w-full h-11 rounded-md border border-slate-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#000000]/30 focus:border-[#000000]" placeholder="admin" /></label>
            <label className="block"><span className="label-caps">Password</span>
              <input data-testid="login-password-input" type="password" value={password} onChange={(e) => setP(e.target.value)} className="mt-1 w-full h-11 rounded-md border border-slate-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#000000]/30 focus:border-[#000000]" placeholder="••••••••" /></label>
            {err && <div data-testid="login-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">{err}</div>}
            <button data-testid="login-submit-button" disabled={busy} className="w-full h-11 rounded-md btn-red font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-60">{busy && <Loader2 className="h-4 w-4 animate-spin" />}Sign in</button>
          </div>
          <div className="mt-10 text-xs text-slate-400" data-testid="login-powered-by">Powered by <a href="https://fundle.ai" target="_blank" rel="noreferrer" className="font-semibold text-slate-700 hover:text-black">Fundle.ai</a></div>
        </form>
      </div>
    </div>
  );
}
